import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';

import { STORAGE_SERVICE_KEYS, StorageService } from '../core/storage.service';
import { IStoredActivePurchaseOrder } from './checkout.service';

export enum CHECKOUT_STEP {
    CS_PAYMENT = 'checkoutStepPayment',
    CS_EXCHANGE_CAR = 'checkoutStepExchangeCar',
    CS_PERSONAL_INFO = 'checkoutStepPersonalInformation',
    CS_INSURANCE = 'checkoutStepInsurance',
    CS_DELIVERY = 'checkoutStepDelivery',
    CS_SUMMARY = 'checkoutStepSummary',
}

export interface ICheckoutFlowStep {
    type: CHECKOUT_STEP;
    title?: string;
    subtitle?: string;
    locked?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class CheckoutFlowService {
    public currentCheckoutStep$ = new ReplaySubject<CHECKOUT_STEP>(1);
    public checkoutFlowOrder$ = new BehaviorSubject<ICheckoutFlowStep[]>([]);
    public atFlowStart$ = new BehaviorSubject<boolean>(true);
    public atFlowEnd$ = new BehaviorSubject<boolean>(true);
    public currentCheckoutStepIndex: number;

    constructor(private storageService: StorageService, private translateService: TranslateService) {
        this.initFlow();
    }

    goToStep(stepType: CHECKOUT_STEP) {
        const _indexOfStep = this.findStepIndex(stepType);
        if (_indexOfStep >= 0) {
            this.currentCheckoutStepIndex = _indexOfStep;
            this.setStep(this.currentCheckoutStepIndex);
        }
    }

    nextStep() {
        if (this.currentCheckoutStepIndex !== undefined && this.currentCheckoutStepIndex < this.checkoutFlowOrder$.value.length - 1) {
            const nextIndex = this.currentCheckoutStepIndex + (this.isStepLocked(this.currentCheckoutStepIndex + 1) ? 2 : 1);
            if (nextIndex <= this.checkoutFlowOrder$.value.length - 1) {
                this.currentCheckoutStepIndex = nextIndex;
                this.setStep(nextIndex);
            }
        }
    }

    prevStep() {
        if (this.currentCheckoutStepIndex !== undefined && this.currentCheckoutStepIndex > 0) {
            const prevIndex = this.currentCheckoutStepIndex - (this.isStepLocked(this.currentCheckoutStepIndex - 1) ? 2 : 1);
            if (prevIndex >= 0) {
                this.currentCheckoutStepIndex = prevIndex;
                this.setStep(prevIndex);
            }
        }
    }

    lockStep(stepType: CHECKOUT_STEP) {
        const _indexOfStep = this.findStepIndex(stepType);
        if (_indexOfStep >= 0) {
            const updatedFlowOrder = this.checkoutFlowOrder$.value?.map((step, INDEX) => (INDEX === _indexOfStep ? { ...step, locked: true } : step));
            this.checkoutFlowOrder$.next(updatedFlowOrder);
        }
    }

    resetFlow() {
        this.initFlow();
        this.goToStep(this.checkoutFlowOrder$.value[0].type);
    }

    private initFlow() {
        this.translateService
            .get([
                'checkout.payment_step____progress_bar_title',
                'checkout.information_step____progress_bar_title',
                'checkout.exchange_car_step____progress_bar_title',
                'checkout.insurance_step____progress_bar_title',
                'checkout.delivery_step____progress_bar_title',
                'checkout.summary_step____progress_bar_title',
            ])
            .pipe(take(1))
            .subscribe((translations: { [key: string]: string }) => {
                this.checkoutFlowOrder$.next([
                    {
                        type: CHECKOUT_STEP.CS_EXCHANGE_CAR,
                        title: translations['checkout.exchange_car_step____progress_bar_title'],
                    },
                    {
                        type: CHECKOUT_STEP.CS_PAYMENT,
                        title: translations['checkout.payment_step____progress_bar_title'],
                    },
                    {
                        type: CHECKOUT_STEP.CS_INSURANCE,
                        title: translations['checkout.insurance_step____progress_bar_title'],
                    },
                    {
                        type: CHECKOUT_STEP.CS_DELIVERY,
                        title: translations['checkout.delivery_step____progress_bar_title'],
                    },
                    {
                        type: CHECKOUT_STEP.CS_PERSONAL_INFO,
                        title: translations['checkout.information_step____progress_bar_title'],
                    },
                    {
                        type: CHECKOUT_STEP.CS_SUMMARY,
                        title: translations['checkout.summary_step____progress_bar_title'],
                    },
                ]);

                const initalStep = this.getActiveStepFromStorage() || this.checkoutFlowOrder$.value[0].type;
                this.goToStep(initalStep);
            });
    }

    private findStepIndex(step: CHECKOUT_STEP) {
        return this.checkoutFlowOrder$.value.findIndex((s) => s.type === step);
    }

    private isStepLocked(index: number) {
        return this.checkoutFlowOrder$.value[index]?.locked;
    }

    private setStep(index: number) {
        const _step = this.checkoutFlowOrder$.value[index].type;
        this.currentCheckoutStep$.next(_step);
        this.setActiveStepInStorage(_step);
        this.setFlowInfoOptions(index);
    }

    private setFlowInfoOptions(index: number) {
        this.atFlowStart$.next(index === 0 || !!(index === 1 && this.isStepLocked(0)));
        this.atFlowEnd$.next(index === this.checkoutFlowOrder$.value?.length - 1);
    }

    private getActivePurchaseOrderFromStorage(): IStoredActivePurchaseOrder | void {
        const activePurchaseOrder = this.storageService.getItem(STORAGE_SERVICE_KEYS.USER_ACTIVE_PURCHASE_ORDER, true);
        if (activePurchaseOrder) return JSON.parse(activePurchaseOrder);
    }

    private getActiveStepFromStorage() {
        const activePurchaseOrder = this.getActivePurchaseOrderFromStorage();
        if (activePurchaseOrder) {
            const { currentCheckoutStep } = activePurchaseOrder;
            return currentCheckoutStep;
        }
    }

    private setActiveStepInStorage(checkoutStep: CHECKOUT_STEP) {
        const activePurchaseOrder = this.getActivePurchaseOrderFromStorage();

        this.storageService.setItem(
            STORAGE_SERVICE_KEYS.USER_ACTIVE_PURCHASE_ORDER,
            JSON.stringify({
                ...activePurchaseOrder,
                currentCheckoutStep: checkoutStep,
            }),
            true
        );
    }
}
